/**
 * Form controls for record editing. Every field renders inside a wrapper with id
 * `field-<name>`, so the gap list on a record page can deep-link straight to it.
 */
import { useId, useRef, useState } from 'react'
import { ArrowUp, ImagePlus, Plus, Trash2, X } from 'lucide-react'
import { api, ApiError, coverUrl } from '../lib/api'
import { useQuery } from '../lib/query'
import type { VocabItem } from '../lib/types'
import { ATTRIBUTABLE_FIELDS, type Attribution, type EntityType, type Link as LinkT } from '../../shared/model'

interface FieldProps {
  name: string
  label: string
  hint?: React.ReactNode
  error?: string
  changed?: boolean
  children: (id: string, describedBy: string | undefined) => React.ReactNode
  wide?: boolean
}

export function Field({ name, label, hint, error, changed, children, wide }: FieldProps) {
  const id = `input-${name}`
  const hintId = hint ? `${id}-hint` : undefined
  const errId = error ? `${id}-err` : undefined
  const describedBy = [hintId, errId].filter(Boolean).join(' ') || undefined
  return (
    <div
      className={`field${wide ? ' is-wide' : ''}${changed ? ' is-changed' : ''}${error ? ' has-error' : ''}`}
      id={`field-${name}`}
    >
      <label className="field-label" htmlFor={id}>
        {label}
        {changed && <span className="changed-dot" aria-label="(changed)" />}
      </label>
      {children(id, describedBy)}
      {hint && (
        <p className="field-hint" id={hintId}>
          {hint}
        </p>
      )}
      {error && (
        <p className="field-error" id={errId} role="alert">
          {error}
        </p>
      )}
    </div>
  )
}

/** Suggestions for an open vocabulary, most used first, as a native datalist. */
export function useVocab(kind: string | undefined): VocabItem[] {
  const { data } = useQuery<{ items: VocabItem[] }>(kind ? `/api/v1/vocab/${kind}` : null, { staleMs: 300_000 })
  return data?.items ?? []
}

export function VocabList({ id, kind, format }: { id: string; kind: string; format?: (v: string) => string }) {
  const items = useVocab(kind)
  return (
    <datalist id={id}>
      {items.map((i) => (
        <option key={i.value} value={i.value}>
          {format ? format(i.value) : undefined}
        </option>
      ))}
    </datalist>
  )
}

/**
 * Several short values (genres, tags, aliases). Enter or comma adds; Backspace on an empty input
 * removes the last chip. Suggestions come from the vocabulary when one is given.
 */
export function ChipsInput({
  id,
  value,
  onChange,
  vocab,
  placeholder,
  describedBy,
}: {
  id: string
  value: string[]
  onChange: (v: string[]) => void
  vocab?: string
  placeholder?: string
  describedBy?: string
}) {
  const [draft, setDraft] = useState('')
  const listId = useId()
  const add = (raw: string) => {
    const v = raw.trim()
    if (!v) return
    if (!value.some((x) => x.toLowerCase() === v.toLowerCase())) onChange([...value, v])
    setDraft('')
  }
  return (
    <div className="chips-input">
      {value.map((v) => (
        <span key={v} className="chip chip-edit">
          {v}
          <button type="button" aria-label={`Remove ${v}`} onClick={() => onChange(value.filter((x) => x !== v))}>
            <X size={12} />
          </button>
        </span>
      ))}
      <input
        id={id}
        value={draft}
        list={vocab ? listId : undefined}
        aria-describedby={describedBy}
        placeholder={value.length ? '' : placeholder}
        onChange={(e) => {
          const v = e.target.value
          if (v.endsWith(',')) add(v.slice(0, -1))
          else setDraft(v)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            add(draft)
          } else if (e.key === 'Backspace' && !draft && value.length) {
            onChange(value.slice(0, -1))
          }
        }}
        onBlur={() => add(draft)}
      />
      {vocab && <VocabList id={listId} kind={vocab} />}
    </div>
  )
}

export function LinksEditor({
  id,
  value,
  onChange,
  labelPlaceholder = 'Official site',
}: {
  id: string
  value: LinkT[]
  onChange: (v: LinkT[]) => void
  labelPlaceholder?: string
}) {
  const set = (i: number, patch: Partial<LinkT>) => onChange(value.map((l, j) => (j === i ? { ...l, ...patch } : l)))
  return (
    <div className="rows">
      {value.map((l, i) => (
        <div key={i} className="row">
          <input
            aria-label={`Link ${i + 1} label`}
            value={l.label}
            placeholder={labelPlaceholder}
            onChange={(e) => set(i, { label: e.target.value })}
            className="row-label"
          />
          <input
            id={i === 0 ? id : undefined}
            aria-label={`Link ${i + 1} address`}
            type="url"
            inputMode="url"
            value={l.url}
            placeholder="https://"
            onChange={(e) => set(i, { url: e.target.value })}
            className="row-grow"
          />
          <button
            type="button"
            className="icon-button"
            aria-label={`Remove link ${i + 1}`}
            onClick={() => onChange(value.filter((_, j) => j !== i))}
          >
            <Trash2 size={16} />
          </button>
        </div>
      ))}
      <button
        type="button"
        id={value.length ? undefined : id}
        className="button button-quiet button-small"
        onClick={() => onChange([...value, { label: '', url: '' }])}
      >
        <Plus size={14} aria-hidden="true" /> Add a link
      </button>
    </div>
  )
}

export function CoverField({
  id,
  value,
  onChange,
  title,
  noun = 'cover',
  note = 'The front cover, PNG, JPEG or WebP, up to 5 MB. Only the cover, never interior pages.',
}: {
  id: string
  value: string | null
  onChange: (v: string | null) => void
  title: string
  /** What the image is, for the button and alt text: "cover", "portrait". */
  noun?: string
  note?: string
}) {
  const input = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const src = coverUrl(value)
  return (
    <div className={`cover-field is-${noun}`}>
      <div className="cover-field-preview">
        {src ? (
          <img src={src} alt={`${noun[0]!.toUpperCase()}${noun.slice(1)} of ${title || 'this record'}`} />
        ) : (
          <span>No {noun}</span>
        )}
      </div>
      <div className="cover-field-actions">
        <input
          ref={input}
          id={id}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif,image/avif"
          className="sr-only"
          onChange={async (e) => {
            const file = e.target.files?.[0]
            e.target.value = ''
            if (!file) return
            setBusy(true)
            setErr('')
            try {
              const form = new FormData()
              form.set('file', file)
              const r = await api<{ key: string }>('/api/v1/uploads', { method: 'POST', body: form })
              onChange(r.key)
            } catch (x) {
              setErr(x instanceof ApiError ? x.message : 'Upload failed')
            } finally {
              setBusy(false)
            }
          }}
        />
        <button type="button" className="button button-quiet" onClick={() => input.current?.click()} disabled={busy}>
          <ImagePlus size={16} aria-hidden="true" />{' '}
          {busy ? 'Uploading…' : src ? `Replace ${noun}` : `Upload a ${noun}`}
        </button>
        {src && (
          <button type="button" className="button button-quiet" onClick={() => onChange(null)}>
            Remove
          </button>
        )}
        <p className="field-hint">{note}</p>
        {err && <p className="field-error">{err}</p>}
      </div>
    </div>
  )
}

export interface PickerItem {
  id?: string
  name: string
  detail?: string
}

/**
 * Find a record by name (systems, books, publishers, creators). With `allowCreate`, a name that
 * matches nothing can be added as a new record, created when the edit is applied.
 */
export function RecordPicker({
  id,
  type,
  onPick,
  allowCreate = false,
  exclude = [],
  placeholder,
  describedBy,
}: {
  id: string
  type: EntityType
  onPick: (item: PickerItem) => void
  allowCreate?: boolean
  exclude?: string[]
  placeholder?: string
  describedBy?: string
}) {
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const listId = useId()
  const term = q.trim()
  const { data } = useQuery<{ items: Array<{ id: string; name: string; detail: string }> }>(
    term.length >= 2 ? `/api/v1/search?q=${encodeURIComponent(term)}&type=${type}&limit=8&explicit=1` : null,
  )
  const hits = (data?.items ?? []).filter((h) => !exclude.includes(h.id))
  const exact = hits.some((h) => h.name.toLowerCase() === term.toLowerCase())
  const options: PickerItem[] = [
    ...hits.map((h) => ({ id: h.id, name: h.name, detail: h.detail })),
    ...(allowCreate && term && !exact ? [{ name: term }] : []),
  ]
  const pick = (item: PickerItem | undefined) => {
    if (!item) return
    onPick(item)
    setQ('')
    setOpen(false)
    setActive(0)
  }
  return (
    <div className="picker">
      <input
        id={id}
        role="combobox"
        aria-expanded={open && options.length > 0}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && options[active] ? `${listId}-${active}` : undefined}
        aria-describedby={describedBy}
        value={q}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(e) => {
          setQ(e.target.value)
          setOpen(true)
          setActive(0)
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            setActive((a) => Math.min(a + 1, options.length - 1))
          } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setActive((a) => Math.max(a - 1, 0))
          } else if (e.key === 'Enter') {
            e.preventDefault()
            pick(options[active])
          } else if (e.key === 'Escape') setOpen(false)
        }}
      />
      {open && term.length >= 2 && (
        <ul id={listId} role="listbox" className="picker-list">
          {options.length === 0 && <li className="picker-empty">No matches</li>}
          {options.map((o, i) => (
            <li
              key={o.id ?? `new:${o.name}`}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === active}
              className="picker-option"
              onMouseDown={(e) => {
                e.preventDefault()
                pick(o)
              }}
              onMouseEnter={() => setActive(i)}
            >
              {o.id ? (
                <>
                  <span>{o.name}</span>
                  {o.detail && <span className="picker-detail">{o.detail}</span>}
                </>
              ) : (
                <>
                  <Plus size={14} aria-hidden="true" /> Add “{o.name}” as a new {type}
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** An ordered list of references with remove and move-to-top, plus a picker to add more. */
export function RefList({
  id,
  type,
  value,
  onChange,
  allowCreate,
  orderHint,
  placeholder,
  describedBy,
}: {
  id: string
  type: EntityType
  value: Array<{ id?: string; name: string }>
  onChange: (v: Array<{ id?: string; name: string }>) => void
  allowCreate?: boolean
  orderHint?: string
  placeholder?: string
  describedBy?: string
}) {
  return (
    <div className="ref-list">
      {value.length > 0 && (
        <ol className="ref-items">
          {value.map((r, i) => (
            <li key={r.id ?? `new:${r.name}`} className="ref-item">
              <span className="ref-name">
                {r.name}
                {!r.id && <span className="ref-new">new</span>}
                {i === 0 && orderHint && value.length > 1 && <span className="ref-primary">{orderHint}</span>}
              </span>
              {i > 0 && (
                <button
                  type="button"
                  className="icon-button"
                  aria-label={`Move ${r.name} to the top`}
                  onClick={() => onChange([r, ...value.filter((_, j) => j !== i)])}
                >
                  <ArrowUp size={16} />
                </button>
              )}
              <button
                type="button"
                className="icon-button"
                aria-label={`Remove ${r.name}`}
                onClick={() => onChange(value.filter((_, j) => j !== i))}
              >
                <X size={16} />
              </button>
            </li>
          ))}
        </ol>
      )}
      <RecordPicker
        id={id}
        type={type}
        allowCreate={allowCreate}
        exclude={value.map((r) => r.id ?? '')}
        placeholder={placeholder}
        describedBy={describedBy}
        onPick={(item) => {
          if (value.some((r) => (item.id ? r.id === item.id : r.name.toLowerCase() === item.name.toLowerCase()))) return
          onChange([...value, item.id ? { id: item.id, name: item.name } : { name: item.name }])
        }}
      />
    </div>
  )
}

/** A set of checkboxes for a closed multi-value vocabulary (play modes, formats). */
export function CheckboxGroup<T extends string>({
  options,
  labels,
  value,
  onChange,
  name,
}: {
  options: readonly T[]
  labels: Record<T, string>
  value: T[]
  onChange: (v: T[]) => void
  name: string
}) {
  return (
    <div className="check-group" role="group" aria-labelledby={`input-${name}-label`}>
      {options.map((o) => (
        <label key={o} className="check">
          <input
            type="checkbox"
            checked={value.includes(o)}
            onChange={(e) => onChange(e.target.checked ? [...value, o] : value.filter((v) => v !== o))}
          />
          <span>{labels[o]}</span>
        </label>
      ))}
    </div>
  )
}

/** Typed links to other records of the same kind: "requires X", "successor of Y". */
export function RelationsEditor<R extends string>({
  id,
  type,
  item,
  value,
  onChange,
  types,
  labels,
  exclude,
}: {
  id: string
  type: EntityType
  item: 'system' | 'book'
  value: Array<{ type: R } & Record<string, unknown>>
  onChange: (v: Array<{ type: R } & Record<string, unknown>>) => void
  types: readonly R[]
  labels: Record<R, string>
  exclude: string[]
}) {
  const [kind, setKind] = useState<R>(types[0]!)
  const refOf = (r: Record<string, unknown>) => r[item] as { id: string; name: string }
  return (
    <div className="rows">
      {value.map((r, i) => (
        <div key={`${r.type}:${refOf(r).id}`} className="row">
          <select
            aria-label="Relation"
            className="row-label"
            value={r.type}
            onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, type: e.target.value as R } : x)))}
          >
            {types.map((t) => (
              <option key={t} value={t}>
                {labels[t]}
              </option>
            ))}
          </select>
          <span className="row-grow ref-name">{refOf(r).name}</span>
          <button
            type="button"
            className="icon-button"
            aria-label={`Remove ${refOf(r).name}`}
            onClick={() => onChange(value.filter((_, j) => j !== i))}
          >
            <Trash2 size={16} />
          </button>
        </div>
      ))}
      <div className="row">
        <select
          aria-label="New relation"
          className="row-label"
          value={kind}
          onChange={(e) => setKind(e.target.value as R)}
        >
          {types.map((t) => (
            <option key={t} value={t}>
              {labels[t]}
            </option>
          ))}
        </select>
        <div className="row-grow">
          <RecordPicker
            id={id}
            type={type}
            exclude={[...exclude, ...value.map((r) => refOf(r).id)]}
            placeholder={`Search ${type === 'book' ? 'books' : 'systems'}…`}
            onPick={(p) => p.id && onChange([...value, { type: kind, [item]: { id: p.id, name: p.name } } as never])}
          />
        </div>
      </div>
    </div>
  )
}

/** Identifiers in other catalogues: a source (with suggestions) and the id there. */
export function ExternalIdsEditor({
  id,
  value,
  onChange,
}: {
  id: string
  value: Array<{ source: string; id: string }>
  onChange: (v: Array<{ source: string; id: string }>) => void
}) {
  const listId = useId()
  const set = (i: number, patch: Partial<{ source: string; id: string }>) =>
    onChange(value.map((e, j) => (j === i ? { ...e, ...patch } : e)))
  return (
    <div className="rows">
      {value.map((e, i) => (
        <div key={i} className="row">
          <input
            aria-label={`Source ${i + 1}`}
            className="row-label mono"
            list={listId}
            value={e.source}
            placeholder="drivethrurpg"
            spellCheck={false}
            onChange={(ev) => set(i, { source: ev.target.value.toLowerCase() })}
          />
          <input
            id={i === 0 ? id : undefined}
            aria-label={`Identifier ${i + 1}`}
            className="row-grow mono"
            value={e.id}
            placeholder="521240"
            spellCheck={false}
            onChange={(ev) => set(i, { id: ev.target.value })}
          />
          <button
            type="button"
            className="icon-button"
            aria-label={`Remove identifier ${i + 1}`}
            onClick={() => onChange(value.filter((_, j) => j !== i))}
          >
            <Trash2 size={16} />
          </button>
        </div>
      ))}
      <VocabList id={listId} kind="external_source" />
      <button
        type="button"
        id={value.length ? undefined : id}
        className="button button-quiet button-small"
        onClick={() => onChange([...value, { source: '', id: '' }])}
      >
        <Plus size={14} aria-hidden="true" /> Add an identifier
      </button>
    </div>
  )
}

/** Series memberships, each with the number in the series ("3", "A1") when it has one. */
export function SeriesRefsEditor({
  id,
  value,
  onChange,
}: {
  id: string
  value: Array<{ id?: string; name: string; number: string }>
  onChange: (v: Array<{ id?: string; name: string; number: string }>) => void
}) {
  return (
    <div className="ref-list">
      {value.length > 0 && (
        <ol className="ref-items">
          {value.map((r, i) => (
            <li key={r.id ?? `new:${r.name}`} className="ref-item">
              <span className="ref-name">
                {r.name}
                {!r.id && <span className="ref-new">new</span>}
              </span>
              <input
                aria-label={`Number in ${r.name}`}
                className="ref-number"
                value={r.number}
                placeholder="No."
                maxLength={20}
                onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, number: e.target.value } : x)))}
              />
              <button
                type="button"
                className="icon-button"
                aria-label={`Remove ${r.name}`}
                onClick={() => onChange(value.filter((_, j) => j !== i))}
              >
                <X size={16} />
              </button>
            </li>
          ))}
        </ol>
      )}
      <RecordPicker
        id={id}
        type="series"
        allowCreate
        exclude={value.map((r) => r.id ?? '')}
        placeholder="Search series and settings…"
        onPick={(p) =>
          onChange([...value, p.id ? { id: p.id, name: p.name, number: '' } : { name: p.name, number: '' }])
        }
      />
    </div>
  )
}

/** A number input that stores `null` when empty. */
export function NumberInput({
  id,
  value,
  onChange,
  min,
  max,
  describedBy,
  placeholder,
  disabled,
}: {
  id: string
  value: number | null
  onChange: (v: number | null) => void
  min?: number
  max?: number
  describedBy?: string
  placeholder?: string
  disabled?: boolean
}) {
  return (
    <input
      id={id}
      aria-describedby={describedBy}
      type="number"
      inputMode="numeric"
      min={min}
      max={max}
      value={value ?? ''}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
    />
  )
}

/**
 * Credits for text that is someone else's writing, shared with their permission: a catalogue the
 * record was imported from, or a source a contributor quotes. Credited fields are not CC0. Any
 * change here is reviewed, so a credit cannot be dropped quietly, nor a link slipped in.
 */
export function CreditedSources({
  value,
  onChange,
  error,
  changed,
}: {
  value: Attribution[]
  onChange: (v: Attribution[]) => void
  error?: string
  changed?: boolean
}) {
  const set = (i: number, patch: Partial<Attribution>) =>
    onChange(value.map((a, j) => (j === i ? { ...a, ...patch } : a)))
  return (
    <Field
      name="attributions"
      label="Credited sources"
      hint="Only for someone else's writing that you have permission to share. Credited fields are not CC0. Changes here are reviewed."
      error={error}
      changed={changed}
      wide
    >
      {(id, describedBy) => (
        <div className="rows" aria-describedby={describedBy}>
          {value.map((a, i) => (
            <div key={i} className="row row-wrap">
              <div className="check-group" role="group" aria-label={`Fields credited to source ${i + 1}`}>
                {ATTRIBUTABLE_FIELDS.map((f) => (
                  <label key={f} className="check">
                    <input
                      type="checkbox"
                      checked={a.fields.includes(f)}
                      onChange={(e) =>
                        set(i, { fields: e.target.checked ? [...a.fields, f] : a.fields.filter((x) => x !== f) })
                      }
                    />
                    <span>{f === 'tags' ? 'Tags' : 'Description'}</span>
                  </label>
                ))}
              </div>
              <input
                id={i === 0 ? id : undefined}
                aria-label={`Source ${i + 1} name`}
                value={a.source}
                placeholder="TTRPG Wiki"
                maxLength={120}
                onChange={(e) => set(i, { source: e.target.value })}
                className="row-label"
              />
              <input
                aria-label={`Source ${i + 1} page`}
                type="url"
                inputMode="url"
                value={a.url}
                placeholder="https://"
                onChange={(e) => set(i, { url: e.target.value })}
                className="row-grow"
              />
              <button
                type="button"
                className="icon-button"
                aria-label={`Remove the credit to ${a.source || `source ${i + 1}`}`}
                onClick={() => onChange(value.filter((_, j) => j !== i))}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          <button
            type="button"
            id={value.length ? undefined : id}
            className="button button-quiet button-small"
            onClick={() => onChange([...value, { fields: ['description'], source: '', url: '' }])}
          >
            <Plus size={14} aria-hidden="true" /> Credit a source
          </button>
        </div>
      )}
    </Field>
  )
}
